import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import {
  DashboardDataTableColumnKind,
  DashboardDataTableSortDirection,
  DashboardTableDialogBody,
  DashboardTableDialogContent,
  DashboardTableDialogDataTable,
  DashboardTableDialogFooter,
  DashboardTableDialogHeader,
  buildDashboardDataTableColumns,
  dashboardTableTextActionClassName,
} from '@shigo-ui/dashboard-ui';
import { Button, Dialog } from '@shigo-ui/ui';

import { dashboardT } from '../../../../../lib/messages';
import { ShigoPermission } from '../../../../auth/permissions';
import { useShigoPermissions } from '../../../../auth/useShigoPermissions';
import { usePlacementsQuery } from '../../../placements/hooks';
import {
  isPlacementSortProperty,
  placementPageSizes,
  PlacementDeliveryType,
  PlacementPricingType,
  PlacementSortDirection,
  PlacementSortProperty,
  type Placement,
  type PlacementsFilter,
} from '../../../placements/model';
import type { Publisher } from '../../model';
import { formatCurrency } from '../../../../../lib/format';

const columns = buildDashboardDataTableColumns<Placement>([
  {
    id: 'name',
    kind: DashboardDataTableColumnKind.Text,
    header: dashboardT('placements.name'),
    headerClassName: 'w-[42%] pl-4',
    className: 'w-[42%] pl-4 font-medium',
    sortable: true,
    sortKey: PlacementSortProperty.Name,
    value: (placement) => <PlacementName placement={placement} />,
  },
  {
    id: 'deliveryType',
    kind: DashboardDataTableColumnKind.Text,
    header: dashboardT('placements.deliveryType'),
    headerClassName: 'w-[22%]',
    className: 'w-[22%]',
    sortable: true,
    sortKey: PlacementSortProperty.DeliveryType,
    value: (placement) => resolveDeliveryTypeLabel(placement.deliveryType),
  },
  {
    id: 'bidFloor',
    kind: DashboardDataTableColumnKind.Text,
    header: dashboardT('placements.floor'),
    headerClassName: 'w-[18%]',
    className: 'w-[18%]',
    sortable: true,
    sortKey: PlacementSortProperty.BidFloor,
    value: (placement) => formatCurrency(placement.bidFloor),
  },
  {
    id: 'cpm',
    kind: DashboardDataTableColumnKind.Text,
    header: dashboardT('placements.cpm'),
    headerClassName: 'w-[18%] pr-4',
    className: 'w-[18%] pr-4',
    value: (placement) =>
      formatCurrency(
        placement.cpm ??
          (placement.pricingType === PlacementPricingType.Cpm ? placement.pricing : undefined),
      ),
  },
]);

export function PublisherPlacementsDialog({
  publisher,
  onOpenChange,
}: {
  publisher: Publisher;
  onOpenChange: (open: boolean) => void;
}) {
  const [filter, setFilter] = useState<PlacementsFilter>({
    searchTerm: '',
    statuses: [],
    types: [],
    publishersIds: [publisher.id],
    page: 0,
    size: 10,
    sortProperty: PlacementSortProperty.Name,
    sortDirection: PlacementSortDirection.Asc,
  });
  const query = usePlacementsQuery(filter);
  const title = dashboardT('publishers.placementsFor', { name: publisher.name });

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DashboardTableDialogContent>
        <DashboardTableDialogHeader title={title} />
        <DashboardTableDialogBody>
          <DashboardTableDialogDataTable
            tableLabel={title}
            columns={columns}
            items={query.data?.content ?? []}
            getRowKey={(placement) => placement.id}
            loading={query.isLoading}
            loadingMessage={dashboardT('placements.loading')}
            errorMessage={query.isError ? dashboardT('placements.loadError') : undefined}
            emptyMessage={dashboardT('placements.empty')}
            minWidthClassName="min-w-[720px]"
            pagination={{
              value: {
                page: filter.page,
                pageSize: filter.size,
                totalItems: query.data?.totalElements ?? 0,
              },
              pageSizes: placementPageSizes,
              disabled: query.isFetching,
              onChange: ({ page, pageSize }) => {
                setFilter((current) => ({
                  ...current,
                  page: pageSize === current.size ? page : 0,
                  size: pageSize,
                }));
              },
            }}
            sort={{
              value: filter.sortProperty
                ? {
                    key: filter.sortProperty,
                    direction:
                      filter.sortDirection === PlacementSortDirection.Asc
                        ? DashboardDataTableSortDirection.Asc
                        : DashboardDataTableSortDirection.Desc,
                  }
                : undefined,
              onChange: (next) => {
                if (!next) {
                  setFilter((current) => ({ ...current, sortProperty: null, page: 0 }));
                  return;
                }
                if (!isPlacementSortProperty(next.key)) return;
                const sortProperty: PlacementSortProperty = next.key;
                setFilter((current) => ({
                  ...current,
                  page: 0,
                  sortProperty,
                  sortDirection:
                    next.direction === DashboardDataTableSortDirection.Asc
                      ? PlacementSortDirection.Asc
                      : PlacementSortDirection.Desc,
                }));
              },
            }}
          />
        </DashboardTableDialogBody>
        <DashboardTableDialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            {dashboardT('placements.close')}
          </Button>
        </DashboardTableDialogFooter>
      </DashboardTableDialogContent>
    </Dialog>
  );
}

function PlacementName({ placement }: { placement: Placement }) {
  const { can } = useShigoPermissions();
  return can(ShigoPermission.SupplyPlacementsEdit) ? (
    <Link
      className={dashboardTableTextActionClassName()}
      to="/supply/placements/edit/$placementId"
      params={{ placementId: placement.id }}
    >
      {placement.name}
    </Link>
  ) : (
    placement.name
  );
}

function resolveDeliveryTypeLabel(deliveryType: PlacementDeliveryType | null | undefined) {
  switch (deliveryType) {
    case PlacementDeliveryType.App:
      return dashboardT('placements.delivery.app');
    case PlacementDeliveryType.Ctv:
      return dashboardT('placements.delivery.ctv');
    case PlacementDeliveryType.Web:
      return dashboardT('placements.delivery.web');
    default:
      return '-';
  }
}
